import { useMemo, useRef, useState } from 'react';
import articleStyles from './article.module.scss';
import Link from 'next/link';
import { useRouter } from 'next/router';
import DocTemplate from '@/components/commonComponents/docTemplate';
import { Tree } from 'antd';
import { debounce } from 'lodash';
import ArticleFeedback from '@/components/docPageComponents/article-feedback/ArticleFeedback';
import ArticleChangeTool from '../docPageComponents/article-change-tool';
import { getDocListByNav } from '@/utils/document/article-and-product';
import ProductGuideLinePopover from './product-guideline-popover';
import SeparatorIcon from '../SvgIcon/icons/SeparatorIcon';
import { DataNode } from 'antd/lib/tree';
import Head from 'next/head';
import { ProductInfo } from '@/services/document';

const recursion = (navigation, callback = (item, path) => {}, path = []) => {
  navigation.map((item) => {
    path.push(item.title);
    item.children && recursion(item.children, callback, path);
    callback(item, path);
    path.pop();
  });
};

// 获取当前选中的目录
const getCurrentNav = (key, navigation) => {
  let titles = [];

  recursion(navigation, (item, path) => {
    if (item.key === key) {
      titles = [...path];
    }
  });

  return titles;
};

export default function Article({
  document,
  navigation,
  product,
  docsKey,
  topDocs,
  cmsConfig,
}: {
  docsKey: string;
  document: { releaseAt: number; draft: string; html: string };
  navigation: any; // ?
  product: { productName: string; productCode: string };
  topDocs: ProductInfo[];
  cmsConfig: {
    hotSearchKeys: string[];
    productTypeOrder: string[];
  };
}) {
  const router = useRouter();
  const activeKey = docsKey;
  const currentNav = getCurrentNav(docsKey, navigation);
  // const [activeKey, setActiveKey] = useState<string>(docsKey);
  // const [currentNav, setCurrentNav] = useState<string[]>(getCurrentNav(docsKey, navigation));
  const [expandedKeys, setExpandedKeys] = useState<string[]>([docsKey]);

  const treeRef = useRef<any>();

  const expandFolderNode = (
    event: React.MouseEvent<HTMLElement>,
    node: any
  ) => {
    const { isLeaf } = node;

    if (isLeaf || event.shiftKey || event.metaKey || event.ctrlKey) {
      return;
    }

    treeRef.current!.onNodeExpand(event as any, node);
  };

  const onDebounceExpand = debounce(expandFolderNode, 200, {
    leading: true,
  });

  const handleSelect = (node, event) => {
    if (node.docId) {
      router.push(`/document/${product.productCode}/${node.docId}`);
    } else {
      // todo 展开
      onDebounceExpand(event, node);
    }
  };

  // 获取文档的一维数组
  const docList = useMemo(() => {
    return getDocListByNav(navigation as any);
  }, [navigation]);

  const handleExpand = (keys) => {
    setExpandedKeys(keys);
  };

  return (
    <div className={articleStyles.wrapper}>
      <div className={articleStyles.container}>
        <div className={articleStyles.navArea}>
          <div className={articleStyles.navWrapper}>
            <div className={articleStyles.navName}>
              <div className={articleStyles.navContent}>
                {product.productName}
              </div>
              <div className={articleStyles.navIcon}>
                <ProductGuideLinePopover
                  productList={topDocs}
                  cmsConfig={cmsConfig}
                />
              </div>
            </div>
            {/* <div className={articleStyles.oneLine}></div> */}
            <div className={articleStyles.navContext}>
              <Tree
                ref={treeRef}
                treeData={navigation}
                selectedKeys={[activeKey]}
                expandedKeys={expandedKeys}
                onSelect={(_, e) => handleSelect(e.node, e)}
                rootClassName={articleStyles.menu}
                onExpand={handleExpand}
                // 强行 rerender, 否则不会响应 defaultExpandKeys 的变化....
                key={activeKey}
                titleRender={(nodeData) => {
                  if ((nodeData as any).docId) {
                    return (
                      <Link
                        href={`/document/${product.productCode}/${
                          (nodeData as any).docId
                        }`}
                        legacyBehavior={false}
                        passHref
                        onClick={(e) => e.preventDefault()}
                        style={{
                          // remove a tag underline
                          color: 'inherit',
                          textDecoration: 'inherit',
                        }}
                      >
                        <span
                          className={
                            articleStyles[
                              `tree-node-level-${(nodeData as any).level}`
                            ]
                          }
                        >
                          {nodeData.title as React.ReactNode}
                        </span>
                      </Link>
                    );
                  }
                  return (
                    <span
                      className={
                        articleStyles[
                          `tree-node-level-${(nodeData as any).level}`
                        ]
                      }
                    >
                      {nodeData.title as React.ReactNode}
                    </span>
                  );
                }}
              />
            </div>
          </div>
        </div>
        <div className={articleStyles.showArea}>
          <div className={articleStyles.articlePath}>
            <Link href={'/document'} passHref>
              <div className={articleStyles.articleCenter}>文档中心</div>
            </Link>
            {currentNav.map((t) => (
              <>
                <SeparatorIcon key={'divider'} />
                <span className={articleStyles.navTitle} key={t}>
                  {t}
                </span>
              </>
            ))}
          </div>
          <Head>
            <title>
              {currentNav[currentNav.length - 1]}-{product.productName}
              -StreamLake
            </title>
          </Head>
          <DocTemplate
            title={currentNav[currentNav.length - 1]}
            time={document.releaseAt}
            content={document.draft}
            html={document.html}
            showAnchor={true}
          />
          {/* 文档底部工具栏 */}
          <div className={articleStyles.tools}>
            <div className={articleStyles.divider} />
            <ArticleChangeTool currentDocId={docsKey} docList={docList} />
            <div className={articleStyles.articleFeedback}>
              <ArticleFeedback
                docId={docsKey}
                docName={currentNav[currentNav.length - 1]}
                productName={product.productName}
                productCode={product.productCode}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
